import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Calendar,
  ArrowRight,
  Star,
  Search,
  ShieldCheck,
  Flame,
  Clock,
  MapPin,
  Mail,
  Phone,
  User,
  Quote,
  Volume2,
  VolumeX,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import GoogleMapsIcon from '../components/GoogleMapsIcon';
import '../styles/HomamPortal.css';
import {
  MOCK_SITE_SETTINGS,
  MOCK_NAKSHATRAS,
  getWhatsAppLink,
  searchNakshatras,
  getNakshatraImage
} from '../data/homamData';
import { getHomamPath } from '../utils/domain';
import HomamSubNavbar from '../components/HomamSubNavbar';
import HomamFlashTicker from '../components/HomamFlashTicker';
import HomamFooter from '../components/HomamFooter';
import VideoPosterModal from '../components/VideoPosterModal';

export function NakshatraHomam() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filterGroup, setFilterGroup] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedStarId, setSelectedStarId] = useState(1);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    whatsappNumber: '',
    location: ''
  });
  const [bookingError, setBookingError] = useState(null);

  // 🎥 Hero Banner Slider & Video Autoplay States
  const [heroSlide, setHeroSlide] = useState(0); // 0: Main Banner, 1: Guruji Video
  const [isHeroPlaying, setIsHeroPlaying] = useState(false);
  const [isHeroMuted, setIsHeroMuted] = useState(true); // Default muted for browser autoplay policy
  const heroVideoRef = useRef(null);

  const promoVideoUrl = '/videos/promo_video_01.mp4';

  // ⏱️ Auto-scroll from Banner to Video (in 6 seconds on banner)
  useEffect(() => {
    let timer;
    if (heroSlide === 0) {
      timer = setTimeout(() => {
        setHeroSlide(1);
      }, 6000);
    }
    return () => clearTimeout(timer);
  }, [heroSlide]);

  // 🎬 Manage video playback automatically based on active slide
  useEffect(() => {
    if (heroSlide === 1) {
      if (heroVideoRef.current) {
        heroVideoRef.current.muted = isHeroMuted;
        heroVideoRef.current.currentTime = 0;
        const playPromise = heroVideoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsHeroPlaying(true))
            .catch((err) => {
              console.log('Video autoplay prevented:', err.message);
              setIsHeroPlaying(false);
            });
        }
      }
    } else {
      if (heroVideoRef.current) {
        heroVideoRef.current.pause();
        setIsHeroPlaying(false);
      }
    }
  }, [heroSlide, isHeroMuted]);

  const toggleHeroPlay = (e) => {
    e?.stopPropagation?.();
    if (!heroVideoRef.current) return;
    if (isHeroPlaying) {
      heroVideoRef.current.pause();
      setIsHeroPlaying(false);
    } else {
      heroVideoRef.current
        .play()
        .then(() => setIsHeroPlaying(true))
        .catch((err) => console.log('Play error:', err));
    }
  };

  const toggleHeroMute = (e) => {
    e?.stopPropagation?.();
    if (!heroVideoRef.current) return;
    const nextMuted = !isHeroMuted;
    heroVideoRef.current.muted = nextMuted;
    setIsHeroMuted(nextMuted);
  };

  const handlePrevHeroSlide = (e) => {
    e?.stopPropagation?.();
    setHeroSlide((prev) => (prev === 0 ? 1 : 0));
  };

  const handleNextHeroSlide = (e) => {
    e?.stopPropagation?.();
    setHeroSlide((prev) => (prev === 0 ? 1 : 0));
  };

  const handleVideoEnded = () => {
    // When video message completes, auto-scroll back to main banner
    setHeroSlide(0);
  };

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const scrollToTarget = () => {
        const el = document.getElementById(id) || document.getElementById('find-star') || document.getElementById('booking-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };

      if (!scrollToTarget()) {
        const timer1 = setTimeout(scrollToTarget, 100);
        const timer2 = setTimeout(scrollToTarget, 300);
        const timer3 = setTimeout(scrollToTarget, 600);
        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
        };
      } else {
        const timer = setTimeout(scrollToTarget, 300);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    document.title = 'Sri 27 Nakshatra Maha Homam | Sri Gnana Sai Baba Mandir';
  }, [location.hash, location.pathname]);

  // Filtered 27 Stars Grid using comprehensive Tanglish & Tamil search
  const displayedNakshatras = searchNakshatras(searchFilter, MOCK_NAKSHATRAS).filter((n) => {
    if (filterGroup === '1-9') return n.orderIndex >= 1 && n.orderIndex <= 9;
    if (filterGroup === '10-18') return n.orderIndex >= 10 && n.orderIndex <= 18;
    if (filterGroup === '19-27') return n.orderIndex >= 19 && n.orderIndex <= 27;
    return true;
  });

  const selectedStar =
    MOCK_NAKSHATRAS.find((n) => n.id === Number(selectedStarId)) || MOCK_NAKSHATRAS[0];

  const handleBookingFormChange = (e) => {
    const { name, value } = e.target;
    setBookingForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'phoneNumber') {
        updated.whatsappNumber = value;
      }
      return updated;
    });
    if (bookingError) setBookingError(null);
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setBookingError(null);

    if (!bookingForm.name.trim() || !bookingForm.email.trim() || !bookingForm.phoneNumber.trim() || !bookingForm.whatsappNumber.trim() || !bookingForm.location.trim()) {
      setBookingError('Please fill in all required fields (Name, Email, Phone Number, WhatsApp Number, Location).');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email.trim())) {
      setBookingError('Please enter a valid email address.');
      return;
    }

    const cleanPhone = bookingForm.phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setBookingError('Please enter a valid 10-digit Phone number.');
      return;
    }

    const cleanWa = bookingForm.whatsappNumber.replace(/\D/g, '');
    if (cleanWa.length < 10) {
      setBookingError('Please enter a valid 10-digit WhatsApp number.');
      return;
    }

    const devoteePayload = {
      name: bookingForm.name.trim(),
      mobile: cleanPhone || cleanWa,
      phoneNumber: cleanPhone,
      whatsappNumber: cleanWa,
      email: bookingForm.email.trim(),
      userId: null,
      nakshatraId: Number(selectedStar.id),
      nakshatraNameEn: selectedStar.nameEn,
      nakshatraNameTa: selectedStar.nameTa,
      homamDate: selectedStar.assignedDate,
      preferredDay: `Day ${selectedStar.orderIndex} • ${selectedStar.assignedDate}`,
      participantsCount: 1,
      city: bookingForm.location.trim(),
      gothram: '',
      message: '',
      source: 'Dedicated Nakshatra Homam Booking Section',
      bookingId: `SGSMH${Date.now().toString().slice(-6)}`,
      _id: 'pending_checkout',
    };

    navigate(getHomamPath('/booking'), {
      state: {
        booking: devoteePayload,
        nakshatra: selectedStar,
      },
    });
  };

  const gurujiWhatsApp = getWhatsAppLink({
    customMessage: `Namaste Guruji Seva Team at Sri Gnana Sai Baba Mandir 🙏\nI would like to seek Guruji's blessings and register for the 27 Nakshatra Maha Homam.`,
    source: 'Guruji Section'
  });


  const timetable = [
    {
      time: '05:50 AM',
      title: 'Temple Open (காலை நடை திறப்பு)',
      desc: 'Morning sanctum doors open for devotees and morning prayers'
    },
    {
      time: '06:15 AM',
      title: 'Kakad Aarti (காக்கட ஆரத்தி)',
      desc: 'Sacred morning awakening Aarti & Mangala Harathi to Sri Sai Baba'
    },
    {
      time: '07:00 AM',
      title: 'Gho Pooja (கோ பூஜை)',
      desc: 'Auspicious Cow Pooja & Kamadhenu worship for peace and prosperity'
    },
    {
      time: '07:30 AM',
      title: 'Sri Baba Abhishekam (பாபா அபிஷேகம்)',
      desc: 'Sacred Panchamrutha & Holy Theertha abhishekam for Sri Gnana Sai Baba'
    },
    {
      time: '09:30 AM – 11:00 AM',
      title: 'Sri Sai Sahasranama Homam (ஸ்ரீ சாய் சஹஸ்ரநாம ஹோமம்)',
      desc: 'Sacred 1,008 divine names chanting & special Nakshatra Ahuti in consecrated Agni Kunda (Devotees attending in person are requested to arrive by 09:00 AM)',
      highlight: true
    },
    {
      time: '12:00 PM',
      title: 'Afternoon Aarti (நண்பகல் ஆரத்தி)',
      desc: 'Madhyan Aarti & midday deeparadhana offering'
    },
    {
      time: '12:30 PM',
      title: 'Maha Annadhanam (மகா அன்னதானம்)',
      desc: 'Distribution of consecrated Prasadam & free holy meals to all devotees',
      highlight: true
    },
    {
      time: '02:00 PM',
      title: 'Temple Closed (மதிய நடை சாத்துதல்)',
      desc: 'Afternoon sanctum rest period'
    },
    {
      time: '04:30 PM',
      title: 'Temple Re-opens (மாலை நடை திறப்பு)',
      desc: 'Evening sanctum doors open for darshan, archana & prayers'
    },
    {
      time: '06:00 PM',
      title: 'Dhoop Aarti (தூப் ஆரத்தி)',
      desc: 'Evening Sandhya Aarti, fragrant dhoop offering & devotional bhajans'
    },
    {
      time: '08:30 PM',
      title: 'Shej Aarti (சேஜ் ஆரத்தி)',
      desc: 'Night divine lullaby Aarti and final day blessings'
    },
    {
      time: '09:00 PM',
      title: 'Temple Closed (இரவு நடை சாத்துதல்)',
      desc: 'Sanctum doors close peacefully for the night'
    }
  ];

  const guidelines = [
    'Traditional Indian attire recommended (Dhoti/Kurta for men, Saree/Salwar for women).',
    'Devotees participating in Homam are advised to maintain light sattvic diet on their star day.',
    'Consecrated Prasadam and Raksha will be dispatched via speed post for remote registered devotees.',
    'Annadhanam (sacred food offering) is served freely to all visitors every afternoon.'
  ];

  return (
    <div className="homam-portal-wrapper">
      {/* 1. Sub Navbar & Topbar */}
      <HomamSubNavbar />

      {/* ⚡ Breaking / Flash Ticker (Nakshatra Schedule Ribbon) */}
      <HomamFlashTicker onSelectStar={(star) => setSelectedStarId(star.id)} />

      {/* ================= 1. FULL WIDTH HERO BANNER SECTION (AUTO-SCROLLING VIDEO SLIDER) ================= */}
      <section
        id="hero"
        className="homam-hero-sec"
      >
        <div className="homam-hero-slider-container">
          {/* Sliding Track */}
          <div
            className="homam-hero-slider-track"
            style={{ transform: `translateX(-${heroSlide * 100}%)` }}
          >
            {/* Slide 1: Main Full-Width Sacred Banner */}
            <div className="homam-hero-slide homam-hero-slide-banner">
              <a
                href="#nakshatras"
                className="homam-hero-banner-link"
                title="Click to view 27 Nakshatra Homam Schedule"
              >
                <img
                  src="/images/nakshatra_homam_home_banner.png"
                  alt="Sri Sai Sahasranama Ayushya Homam - Sri Gnana Sai Baba Mandir"
                  className="homam-hero-banner-img"
                />
              </a>

              {/* Floating Action Button Inside Banner */}
              <div className="homam-banner-inner-actions">
                <a
                  href="#find-star"
                  className="btn-hero-overlay-primary"
                  aria-label="Book for 27 Nakshatra Maha Homam"
                >
                  <Flame className="hero-btn-icon" />
                  <span className="hero-btn-label-desktop">Book for Homam</span>
                  <span className="hero-btn-label-mobile">Book Homam</span>
                  <ArrowRight className="hero-btn-arrow" />
                </a>
              </div>
            </div>

            {/* Slide 2: Guruji's Sacred Anugraha Bhashanam Video Slide (Exact Same Size as Banner) */}
            <div className="homam-hero-slide homam-hero-slide-video">
              <video
                ref={heroVideoRef}
                src={promoVideoUrl}
                playsInline
                muted={isHeroMuted}
                preload="auto"
                onPlay={() => setIsHeroPlaying(true)}
                onPause={() => setIsHeroPlaying(false)}
                onEnded={handleVideoEnded}
                onError={(e) => {
                  if (!e.target.src.includes('/videos/promo_video_01.mp4')) {
                    e.target.src = '/videos/promo_video_01.mp4';
                  }
                }}
                className="homam-hero-video-elem"
              />

              {/* Video Badge Overlay */}
              <div className="homam-hero-video-badge-overlay">
                <Flame className="w-4 h-4 text-amber-400 animate-pulse" />
                <span>GURUJI'S SACRED ANUGRAHA BHASHANAM VIDEO</span>
              </div>

              {/* Center Play Button Overlay when Paused */}
              {!isHeroPlaying && (
                <button
                  type="button"
                  onClick={toggleHeroPlay}
                  className="homam-hero-video-center-btn"
                  title="Play Sacred Video"
                  aria-label="Play Sacred Video"
                >
                  <Play className="w-9 h-9 text-amber-300 fill-amber-300 ml-1" />
                </button>
              )}

              {/* Minimal Icon Controls: Small Pause/Play and Mute/Unmute */}
              <div className="homam-video-minimal-controls">
                <button
                  type="button"
                  onClick={toggleHeroPlay}
                  className="homam-video-icon-btn"
                  title={isHeroPlaying ? "Pause Video" : "Play Video"}
                  aria-label={isHeroPlaying ? "Pause Video" : "Play Video"}
                >
                  {isHeroPlaying ? (
                    <Pause className="w-4 h-4 text-amber-300" />
                  ) : (
                    <Play className="w-4 h-4 text-amber-300 fill-amber-300 ml-0.5" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={toggleHeroMute}
                  className={`homam-video-icon-btn ${isHeroMuted ? 'muted-pulse' : ''}`}
                  title={isHeroMuted ? "Unmute Audio" : "Mute Audio"}
                  aria-label={isHeroMuted ? "Unmute Audio" : "Mute Audio"}
                >
                  {isHeroMuted ? (
                    <VolumeX className="w-4 h-4 text-amber-300" />
                  ) : (
                    <Volume2 className="w-4 h-4 text-emerald-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            type="button"
            onClick={handlePrevHeroSlide}
            className="homam-hero-arrow homam-hero-arrow-prev"
            aria-label="Previous Slide"
            title="Previous Slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            type="button"
            onClick={handleNextHeroSlide}
            className="homam-hero-arrow homam-hero-arrow-next"
            aria-label="Next Slide"
            title="Next Slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Carousel Indicator Dots */}
          <div className="homam-hero-dots">
            <button
              type="button"
              onClick={() => setHeroSlide(0)}
              className={`homam-hero-dot ${heroSlide === 0 ? 'active' : ''}`}
              aria-label="Slide 1: Sacred Homam Banner"
              title="Sacred Homam Banner"
            />
            <button
              type="button"
              onClick={() => setHeroSlide(1)}
              className={`homam-hero-dot ${heroSlide === 1 ? 'active' : ''}`}
              aria-label="Slide 2: Guruji Video Message"
              title="Guruji Video Message"
            />
          </div>
        </div>

        {/* Mobile-Only: Book Now CTA Button below Hero Carousel */}
        <div className="homam-hero-mobile-cta-bar">
          <a
            href="#find-star"
            className="btn-hero-mobile-cta"
            aria-label="Book Now for Sri 27 Nakshatra Maha Homam"
          >
            <Flame className="w-4 h-4" />
            <span>Book Now</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* ================= 3. BOOKING FOR HOMAM WITH DETAILS ================= */}
      <section id="find-star" className="homam-grid-wrap" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F4EFE6 50%, #FAF7F2 100%)', borderTop: '1px solid #EADDC7' }}>
        <div className="homam-container">
          <div className="homam-grid-head">
            <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
              <span>Sacred Homam Registration</span>
            </div>
            <h2>Book for 27 Nakshatra Maha Homam</h2>
            <p className="tamil-sub">உங்கள் ஜென்ம நட்சத்திர ஹோமத்திற்கு இப்போதே முன்பதிவு செய்யுங்கள்</p>
            <p>
              Enter your devotee details below to register for the sacred homam. Select your birth star to participate on its dedicated day.
            </p>
          </div>

          {/* Master Sacred Sanctuary Console - Professional Unified Layout */}
          <div className="homam-console-wrapper">
            <div id="booking-form" className="unified-console-wrapper">
              {/* Top Sacred Altar Banner */}
              <div className="unified-top-altar">
                <div className="unified-altar-left">
                  <div className="unified-altar-avatar-ring">
                    <img
                      src={selectedStar.deityImage || getNakshatraImage(selectedStar.orderIndex)}
                      alt={selectedStar.nameEn}
                      className="unified-altar-avatar-img"
                      onError={(e) => {
                        e.currentTarget.src = '/images/nakshatra_homam_floating.jpg';
                      }}
                    />
                  </div>
                  <div className="unified-altar-info">
                    <div className="unified-altar-timing-badge">
                      <Calendar className="w-3 h-3" style={{ color: '#b8860b' }} />
                      <span>DAY {selectedStar.orderIndex} • {selectedStar.assignedDate} • 9:00 AM – 11:00 AM</span>
                    </div>
                    <h3 className="unified-altar-star-title">
                      {selectedStar.nameEn}
                      <span className="unified-altar-star-ta">({selectedStar.nameTa})</span>
                    </h3>
                    <div className="unified-altar-mantra-pill">
                      <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                      <span>Moola Mantra: &ldquo;{selectedStar.moolaMantra}&rdquo;</span>
                    </div>
                  </div>
                </div>

                <div className="unified-altar-controls">
                  <label style={{ fontSize: 12, color: '#475569', fontWeight: 800 }}>
                    Select / Switch Dedicated Star:
                  </label>
                  <select
                    value={selectedStarId}
                    onChange={(e) => setSelectedStarId(Number(e.target.value))}
                    className="unified-star-select"
                  >
                    {MOCK_NAKSHATRAS.map((star) => (
                      <option key={star.id} value={star.id}>
                        Day {star.orderIndex}: {star.nameEn} ({star.nameTa}) — {star.assignedDate}
                      </option>
                    ))}
                  </select>

                  <Link
                    to={`/nakshatra-homam/nakshatra/${selectedStar.slug}`}
                    className="unified-star-details-link"
                  >
                    <span>View {selectedStar.nameEn} Star Day Details</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Bottom Form Grid */}
              <form onSubmit={handleBookingSubmit} className="unified-form-grid">
                {bookingError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '11px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                    {bookingError}
                  </div>
                )}

                <div className="unified-row-3col">
                  <div className="unified-field">
                    <label>
                      <User className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                      <span>Devotee Name *</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full name"
                      value={bookingForm.name}
                      onChange={handleBookingFormChange}
                      required
                    />
                  </div>

                  <div className="unified-field">
                    <label>
                      <Mail className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                      <span>Email Address *</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="devotee@gmail.com"
                      value={bookingForm.email}
                      onChange={handleBookingFormChange}
                      required
                    />
                  </div>

                  <div className="unified-field">
                    <label>
                      <MapPin className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                      <span>City / Location *</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      placeholder="e.g. Chennai, Bengaluru"
                      value={bookingForm.location}
                      onChange={handleBookingFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="unified-row-action">
                  <div className="unified-field">
                    <label>
                      <Phone className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                      <span>Phone Number *</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      maxLength={10}
                      placeholder="10-digit phone number"
                      value={bookingForm.phoneNumber}
                      onChange={handleBookingFormChange}
                      required
                    />
                  </div>

                  <div className="unified-field">
                    <label>
                      <WhatsAppIcon size={14} style={{ color: '#25D366' }} />
                      <span>WhatsApp Number *</span>
                    </label>
                    <input
                      type="tel"
                      name="whatsappNumber"
                      maxLength={10}
                      placeholder="10-digit number"
                      value={bookingForm.whatsappNumber}
                      onChange={handleBookingFormChange}
                      required
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="btn-unified-submit"
                    >
                      <span>Book for Homam</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4. THE 27 NAKSHATRA CALENDAR GRID (Screenshot 4) ================= */}
      <section id="nakshatras" className="homam-grid-wrap" style={{ borderTop: '1px solid #EADDC7' }}>
        <div className="homam-container">
          <div className="homam-grid-head">
            <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
              <Calendar className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
              <span>27 DAYS MAHA HOMAM SCHEDULE</span>
            </div>
            <h2>The 27 Nakshatra Calendar Grid</h2>
            <p className="tamil-sub">27 நாட்களுக்கான முழுமையான நட்சத்திர ஹோம அட்டவணை</p>
            <p>One sacred star is honored on each dedicated day. Explore each day’s spiritual blessings, deity offerings, and register for your family’s Homam.</p>
          </div>

          {/* Filter Bar */}
          <div className="homam-filter-container">
            <div className="homam-tabs-group">
              <button onClick={() => setFilterGroup('all')} className={`homam-filter-btn ${filterGroup === 'all' ? 'active' : ''}`}>All 27 Days</button>
              <button onClick={() => setFilterGroup('1-9')} className={`homam-filter-btn ${filterGroup === '1-9' ? 'active' : ''}`}>Days 1–9 (Shatabhisha – Mrigashira)</button>
              <button onClick={() => setFilterGroup('10-18')} className={`homam-filter-btn ${filterGroup === '10-18' ? 'active' : ''}`}>Days 10–18 (Ardra – Chitra)</button>
              <button onClick={() => setFilterGroup('19-27')} className={`homam-filter-btn ${filterGroup === '19-27' ? 'active' : ''}`}>Days 19–27 (Swati – Dhanishta)</button>
            </div>

            <div className="homam-search-box">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder="Search star or deity..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>
          </div>

          {/* 27 Cards Grid */}
          <div className="homam-27-cards-grid">
            {displayedNakshatras.map((nak) => {
              const isClosed = nak.registrationStatus && !nak.registrationStatus.canRegister;

              return (
                <Link
                  key={nak.id}
                  to={`/nakshatra-homam/nakshatra/${nak.slug}`}
                  className={`homam-star-item-card ${isClosed ? 'homam-card-closed' : ''}`}
                  style={isClosed ? { opacity: 0.82 } : {}}
                  title={`Click to View Full Details for ${nak.nameEn} (${nak.nameTa})`}
                >
                  {/* Top Image Banner with Glassmorphism Badges */}
                  <div className="card-star-img-wrapper">
                    <img
                      src={getNakshatraImage(nak)}
                      alt={`${nak.nameEn} - ${nak.deity}`}
                      className="card-star-img"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/images/nakshatra_homam_floating.jpg';
                      }}
                    />
                    <div className="card-star-img-overlay" />

                    {/* Glassmorphism Day Pill Top-Left (Compact) */}
                    <span className="card-glass-badge card-glass-day">
                      <Star className="w-2.5 h-2.5" style={{ width: 10, height: 10, fill: '#fef08a', color: '#fef08a' }} />
                      <span>DAY {nak.orderIndex}</span>
                    </span>

                    {/* Glassmorphism Date Pill Top-Right (Compact) */}
                    <span className="card-glass-badge card-glass-date">
                      <Calendar className="w-2.5 h-2.5" style={{ width: 10, height: 10, color: '#92400e', flexShrink: 0 }} />
                      <span>{nak.assignedDate}</span>
                    </span>
                  </div>

                  {/* Card Content Below Image */}
                  <div className="card-star-body">
                    {/* Star Name (EN + TA) */}
                    <div className="card-star-titles">
                      <h3 className="card-star-en">{nak.nameEn}</h3>
                      <span className="card-star-ta">{nak.nameTa}</span>
                    </div>

                    {/* Details Row: Left (Deity & Planet) + Right (Arrow Button below Tamil Name) */}
                    <div className="card-star-details-row">
                      <div className="card-meta-chips">
                        <div className="card-meta-row">
                          <span>Deity:</span>
                          <strong>{nak.deity.split('(')[0]}</strong>
                        </div>
                        <div className="card-meta-row">
                          <span>Planet:</span>
                          <strong>{nak.rulingPlanet} ({nak.rulingPlanetTa})</strong>
                        </div>
                      </div>

                      {/* Arrow Icon positioned on the right directly below Tamil name */}
                      <div className="card-arrow-circle" title={`View ${nak.nameEn} Details`}>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>



      {/* ================= 5. GURUJI SECTION (Screenshot 5) ================= */}
      <section className="homam-grid-wrap" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F4EFE6 50%, #FAF7F2 100%)', borderTop: '1px solid #EADDC7' }}>
        <div className="homam-container">
          <div className="homam-guruji-grid">
            {/* Left Image & Video Poster */}
            <div style={{ textAlign: 'center', maxWidth: 440, margin: '0 auto' }}>
              <div style={{ position: 'relative', borderRadius: 28, overflow: 'hidden', border: '3px solid rgba(245, 197, 66, 0.9)', boxShadow: '0 8px 30px rgba(184, 134, 11, 0.2)', background: '#0a192f' }}>
                <img
                  src="/images/guruji.jpg"
                  alt="Guruji - Sri Gnana Sai Baba Mandir"
                  style={{ width: '100%', height: 430, objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                />
              </div>

              {/* Video Message Player Poster */}
              <VideoPosterModal
                title="Guruji's Sacred Anugraha Bhashanam"
                subtitle="Watch Guruji explain the profound power of 27 Nakshatra Homam"
                posterUrl="/images/sai_sahasranama_ayushya_homam_banner.jpg"
                videoEmbedUrl="/videos/promo_video_01.mp4"
                aspectRatio="16/9"
              />
            </div>

            {/* Right Content */}
            <div style={{ textAlign: 'left' }}>
              <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                <span>DIVINE GUIDANCE & LEADERSHIP</span>
              </div>

              <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f2d5c', margin: '8px 0 16px 0', lineHeight: 1.2 }}>
                Under the Benevolent Grace of <br />
                <span className="gold-text">{MOCK_SITE_SETTINGS.gurujiName}</span>
              </h2>

              {/* Quote Block */}
              <div style={{ background: '#ffffff', borderRadius: 20, border: '2px solid rgba(245, 197, 66, 0.8)', padding: 22, position: 'relative', boxShadow: '0 4px 18px rgba(184, 134, 11, 0.1)', marginBottom: 18 }}>
                <Quote className="w-7 h-7" style={{ color: '#fde68a', position: 'absolute', top: 16, right: 16 }} />
                <p style={{ fontStyle: 'italic', fontSize: 13.5, color: '#1e293b', lineHeight: 1.6, margin: 0 }}>
                  &ldquo;The sacred fire at Sri Gnana Sai Baba Mandir does not just burn wood — it burns away our past karma, worries, and life obstacles. When all 27 Nakshatras are prayed to together, you and your family are blessed with good health, happiness, and divine peace.&rdquo;
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                  <Flame className="w-4 h-4" style={{ color: '#b8860b' }} />
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#946608' }}>— {MOCK_SITE_SETTINGS.gurujiName}</span>
                </div>
              </div>

              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>
                {MOCK_SITE_SETTINGS.gurujiBioShort}
              </p>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
                For this sacred 27-day Maha Homam, Guruji has personally blessed the Agni Kundas, selected pure herbal offerings (Samithu), and arranged experienced Vedic scholars to perform continuous prayers and holy rituals for all devotees.
              </p>

              {/* 3 Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>35+ Yrs</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Vedic Tapasya</span>
                </div>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>108+</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Grand Yagnas</span>
                </div>
                <div style={{ background: '#ffffff', padding: '12px 8px', borderRadius: 16, border: '1px solid #EADDC7', textAlign: 'center' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'block' }}>50,000+</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Devotees Blessed</span>
                </div>
              </div>

              {/* Action CTA: Gold Accent Button */}
              <div>
                <a
                  href={gurujiWhatsApp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-hero-guruji"
                >
                  <WhatsAppIcon size={18} style={{ color: '#0a192f', marginRight: 8 }} />
                  <span>Connect with Guruji's Seva Team</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* ================= 7. TIMETABLE & VENUE GUIDE ================= */}
      <section className="homam-grid-wrap" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F4EFE6 50%, #FAF7F2 100%)', borderTop: '1px solid #EADDC7' }}>
        <div className="homam-container">
          <div className="homam-grid-head">
            <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
              <Clock className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
              <span>Event Information & Guidelines</span>
            </div>
            <h2>Daily Timetable & Venue Guide</h2>
            <p className="tamil-sub">தினசரி யாக நேர அட்டவணை மற்றும் பக்தர்களுக்கான வழிகாட்டுதல்கள்</p>
          </div>

          <div className="homam-timetable-venue-grid">
            {/* Timeline */}
            <div className="homam-timetable-box">
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 18px 0' }}>
                <Clock className="w-5 h-5" style={{ color: '#b8860b' }} />
                <span>Daily Yagna Timeline (Every Day for 27 Days)</span>
              </h3>

              <div className="homam-timetable-items-grid">
                {timetable.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: 12,
                      borderRadius: 14,
                      background: item.highlight ? '#FFFDF0' : '#FAF7F2',
                      border: item.highlight ? '1.5px solid #d4af37' : '1px solid #EADDC7',
                      boxShadow: item.highlight ? '0 2px 10px rgba(184, 134, 11, 0.08)' : 'none',
                      boxSizing: 'border-box'
                    }}
                  >
                    <span
                      style={{
                        background: item.highlight ? '#fef3c7' : '#fef3c7',
                        border: item.highlight ? '1px solid #b8860b' : '1px solid #fde68a',
                        color: item.highlight ? '#b8860b' : '#92400e',
                        fontSize: 11,
                        fontWeight: 800,
                        padding: '4px 8px',
                        borderRadius: 8,
                        fontFamily: 'monospace',
                        flexShrink: 0
                      }}
                    >
                      {item.time}
                    </span>
                    <div>
                      <h4 style={{ margin: '0 0 2px 0', fontSize: 13, fontWeight: 800, color: item.highlight ? '#946608' : '#0f2d5c' }}>
                        {item.title}
                      </h4>
                      <p style={{ margin: 0, fontSize: 11.5, color: '#475569', lineHeight: 1.4 }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Venue & Guidelines */}
            <div className="homam-venue-guidelines-col">
              <div className="homam-venue-card">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px 0' }}>
                  <MapPin className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Mandir & Yagna Shala Location</span>
                </h3>

                <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, margin: '0 0 12px 0' }}>
                  <strong style={{ display: 'block', color: '#0f2d5c', fontSize: 15, fontWeight: 800 }}>{MOCK_SITE_SETTINGS.mandirName}</strong>
                  {MOCK_SITE_SETTINGS.venueAddress}, <br />
                  {MOCK_SITE_SETTINGS.venueCity}
                </p>

                <div style={{ marginBottom: 14 }}>
                  <a
                    href={MOCK_SITE_SETTINGS.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: '#b8860b',
                      textDecoration: 'none',
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <GoogleMapsIcon size={16} />
                    <span>View on Google Maps</span>
                  </a>
                </div>

                <div style={{ borderTop: '1px solid #EADDC7', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5, color: '#475569' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#0f2d5c', marginBottom: 4 }}>
                      <Phone className="w-4 h-4" style={{ color: '#b8860b' }} />
                      <span>Helpline & Enquiry Numbers:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, paddingLeft: 24 }}>
                      {(MOCK_SITE_SETTINGS.phoneNumbers || [MOCK_SITE_SETTINGS.phoneNumber]).map((phone, idx) => (
                        <a
                          key={idx}
                          href={`tel:${phone.replace(/\s+/g, '')}`}
                          style={{ color: '#0f2d5c', textDecoration: 'none', fontWeight: 600 }}
                        >
                          📞 {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#0f2d5c', marginBottom: 4 }}>
                      <Mail className="w-4 h-4" style={{ color: '#b8860b' }} />
                      <span>Email IDs:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, paddingLeft: 24 }}>
                      {(MOCK_SITE_SETTINGS.emails || [MOCK_SITE_SETTINGS.email]).map((email, idx) => (
                        <a
                          key={idx}
                          href={`mailto:${email}`}
                          style={{ color: '#0f2d5c', textDecoration: 'none', fontWeight: 600 }}
                        >
                          ✉️ {email} {idx === 0 && <span style={{ fontSize: 12, color: '#b8860b', fontWeight: 700 }}>(Main)</span>}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="homam-guidelines-card">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 14px 0' }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Guidelines for Devotees</span>
                </h3>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14.5, color: '#334155', flex: 1, justifyContent: 'space-around' }}>
                  {guidelines.map((g, idx) => (
                    <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#d4a017', marginTop: 7, flexShrink: 0 }} />
                      <span style={{ lineHeight: 1.6, fontWeight: 500 }}>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Dedicated Homam Footer */}
      <HomamFooter />
    </div>
  );
}

export default NakshatraHomam;
